import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import I18n from "../../i18n"
import { acceptsAttachment } from "./attachment_types.mjs"

const t = (key, options) => I18n.t(`erp.sales.js.dropzone.${key}`, options)

export default class extends Controller {
  static targets = ["input", "fileList", "dropzone", "hiddenInputs", "status"]
  static values = { url: String, formId: String, maxFileSize: { type: Number, default: 20 * 1024 * 1024 } }

  connect() {
    this.entries = []
  }

  // The zone's dashed line turns black while files hover it
  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.replace("border-erp-line-dashed", "border-erp-black")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.replace("border-erp-black", "border-erp-line-dashed")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.replace("border-erp-black", "border-erp-line-dashed")
    this.addFiles(event.dataTransfer.files)
  }

  browse() {
    this.inputTarget.click()
  }

  inputChanged() {
    this.addFiles(this.inputTarget.files)
    this.inputTarget.value = ""
  }

  addFiles(fileList) {
    for (const file of fileList) {
      if (this.isDuplicate(file)) continue

      const entry = { file, state: "pending", progress: 0, signedId: null, previewUrl: null }

      if (!acceptsAttachment(file, this.inputTarget.accept)) {
        entry.state = "error"
        entry.errorMessage = t("unsupported_type")
      } else if (file.size > this.maxFileSizeValue) {
        entry.state = "error"
        entry.errorMessage = t("file_too_large", { size: this.formatSize(this.maxFileSizeValue) })
      }

      if (file.type.startsWith("image/")) {
        entry.previewUrl = URL.createObjectURL(file)
      }

      this.entries.push(entry)
      if (entry.state !== "error") this.uploadEntry(entry)
    }
    this.render()
  }

  uploadEntry(entry) {
    entry.state = "uploading"
    const upload = new DirectUpload(entry.file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            entry.progress = Math.round((event.loaded / event.total) * 100)
            this.render()
          }
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        entry.state = "error"
        entry.errorMessage = error.message || t("upload_failed")
        console.warn("[dropzone] Upload failed:", error)
      } else {
        entry.state = "complete"
        entry.signedId = blob.signed_id
      }
      this.render()
      this.syncHiddenInputs()
      this.maybeAutoSubmit()
    })
  }

  removeEntry(event) {
    const index = parseInt(event.params.index)
    const entry = this.entries[index]
    if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl)
    this.entries.splice(index, 1)
    this.render()
    this.syncHiddenInputs()
    this.maybeAutoSubmit()
  }

  render() {
    this.fileListTarget.replaceChildren()
    this.updateStatus()

    if (this.entries.length === 0) return

    const grid = document.createElement("div")
    grid.className = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-3"

    this.entries.forEach((entry, index) => {
      const card = document.createElement("div")
      card.className = "relative group overflow-hidden rounded-[14px] border border-erp-line bg-erp-surface"

      // Preview area
      const previewWrap = document.createElement("div")
      previewWrap.className = "relative aspect-square flex items-center justify-center bg-white"

      if (entry.previewUrl) {
        const img = document.createElement("img")
        img.src = entry.previewUrl
        img.className = "w-full h-full object-cover"
        img.alt = entry.file.name
        previewWrap.appendChild(img)
      } else {
        const fileIcon = document.createElement("i")
        fileIcon.className = "lni lni-file-text text-3xl text-erp-muted"
        previewWrap.appendChild(fileIcon)
      }

      // Progress overlay
      if (entry.state === "uploading") {
        const overlay = document.createElement("div")
        overlay.className = "absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1"

        const pctText = document.createElement("span")
        pctText.className = "text-white text-sm font-semibold"
        pctText.textContent = `${entry.progress}%`
        overlay.appendChild(pctText)

        const barWrap = document.createElement("div")
        barWrap.className = "w-3/4 h-1.5 bg-white/30 rounded-full overflow-hidden"
        const bar = document.createElement("div")
        bar.className = "h-full bg-white rounded-full transition-all"
        bar.style.width = `${entry.progress}%`
        barWrap.appendChild(bar)
        overlay.appendChild(barWrap)

        previewWrap.appendChild(overlay)
      } else if (entry.state === "complete") {
        const badge = document.createElement("div")
        badge.className = "absolute top-1.5 right-1.5 w-5 h-5 bg-erp-st-won-fg rounded-full flex items-center justify-center"
        const checkIcon = document.createElement("i")
        checkIcon.className = "lni lni-check text-white text-xs"
        badge.appendChild(checkIcon)
        previewWrap.appendChild(badge)
      } else if (entry.state === "error") {
        const badge = document.createElement("div")
        badge.className = "absolute top-1.5 right-1.5 w-5 h-5 bg-erp-danger rounded-full flex items-center justify-center"
        badge.title = entry.errorMessage || t("error")
        const errIcon = document.createElement("i")
        errIcon.className = "lni lni-error text-white text-xs"
        badge.appendChild(errIcon)
        previewWrap.appendChild(badge)
      }

      card.appendChild(previewWrap)

      // Info bar
      const info = document.createElement("div")
      info.className = "flex items-center justify-between px-2 py-1.5"

      const nameWrap = document.createElement("div")
      nameWrap.className = "min-w-0 flex-1"
      const nameEl = document.createElement("p")
      nameEl.className = "text-xs font-semibold text-erp-ink truncate"
      nameEl.textContent = entry.file.name
      const sizeEl = document.createElement("p")
      sizeEl.className = "text-[11px] text-erp-muted"
      sizeEl.textContent = this.formatSize(entry.file.size)
      nameWrap.appendChild(nameEl)
      nameWrap.appendChild(sizeEl)
      if (entry.errorMessage) {
        const errorEl = document.createElement("p")
        errorEl.className = "text-xs font-semibold text-erp-danger"
        errorEl.textContent = entry.errorMessage
        nameWrap.appendChild(errorEl)
      }

      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.className = "shrink-0 cursor-pointer rounded-full p-0.5 text-erp-danger hover:bg-erp-surface-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-erp-black"
      removeBtn.dataset.action = "erp--dropzone#removeEntry"
      removeBtn.dataset["erpDropzoneIndexParam"] = index
      removeBtn.setAttribute("aria-label", t("remove_file", { filename: entry.file.name }))
      const removeIcon = document.createElement("i")
      removeIcon.className = "lni lni-xmark-circle text-base"
      removeIcon.setAttribute("aria-hidden", "true")
      removeBtn.appendChild(removeIcon)

      info.appendChild(nameWrap)
      info.appendChild(removeBtn)
      card.appendChild(info)

      grid.appendChild(card)
    })

    this.fileListTarget.appendChild(grid)
  }

  syncHiddenInputs() {
    this.hiddenInputsTarget.replaceChildren()

    this.entries.forEach((entry) => {
      if (entry.signedId) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "signed_ids[]"
        input.value = entry.signedId
        if (this.hasFormIdValue) input.setAttribute("form", this.formIdValue)
        this.hiddenInputsTarget.appendChild(input)
      }
    })
  }

  updateStatus() {
    if (!this.hasStatusTarget) return

    if (this.entries.length === 0) {
      this.statusTarget.classList.add("hidden")
      return
    }

    this.statusTarget.classList.remove("hidden")
    const total = this.entries.length
    const done = this.entries.filter(e => e.state === "complete").length
    const errors = this.entries.filter(e => e.state === "error").length
    const uploading = total - done - errors

    const textEl = this.statusTarget.querySelector("[data-role='text']")
    const spinnerEl = this.statusTarget.querySelector("[data-role='spinner']")

    if (uploading > 0) {
      this.statusTarget.setAttribute("role", "status")
      this.statusTarget.setAttribute("aria-live", "polite")
      textEl.textContent = t("uploading", { done, total })
      if (spinnerEl) spinnerEl.classList.remove("hidden")
    } else if (errors > 0) {
      this.statusTarget.setAttribute("role", "alert")
      this.statusTarget.setAttribute("aria-live", "assertive")
      textEl.textContent = t("upload_errors", { done, total, errors })
      if (spinnerEl) spinnerEl.classList.add("hidden")
    } else {
      this.statusTarget.setAttribute("role", "status")
      this.statusTarget.setAttribute("aria-live", "polite")
      textEl.textContent = t("upload_complete", { total })
      if (spinnerEl) spinnerEl.classList.add("hidden")
    }
  }

  maybeAutoSubmit() {
    const hasEntries = this.entries.length > 0
    const allComplete = this.entries.every(e => e.state === "complete")

    // Keep a partially failed batch visible so the failed files cannot disappear behind
    // an automatic submission of only the successful subset.
    if (hasEntries && allComplete) {
      const form = this.hasFormIdValue ? document.getElementById(this.formIdValue) : this.element.closest("form")
      if (form) form.requestSubmit()
    }
  }

  isDuplicate(file) {
    return this.entries.some(e => e.file.name === file.name && e.file.size === file.size)
  }

  formatSize(bytes) {
    if (bytes < 1024) return t("bytes", { size: bytes })
    if (bytes < 1024 * 1024) return t("kilobytes", { size: (bytes / 1024).toFixed(1) })
    return t("megabytes", { size: (bytes / (1024 * 1024)).toFixed(1) })
  }

  disconnect() {
    this.entries.forEach(e => {
      if (e.previewUrl) URL.revokeObjectURL(e.previewUrl)
    })
  }
}
